import { isAxiosError } from 'axios';

export type AccountDeletionPhase = 'pending' | 'deleting' | 'completed' | 'failed';

export interface AccountDeletionResult {
  phase: AccountDeletionPhase;
}

export type AccountDeletionErrorCode =
  | 'in_progress'
  | 'protected'
  | 'invalid_confirmation'
  | 'incomplete'
  | 'rejected';

export interface AccountDeletionFailure {
  code: AccountDeletionErrorCode;
  message: string;
  retryable: boolean;
  httpStatus?: number;
  requestId?: string;
}

function safeRequestId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return /^[a-zA-Z0-9._:-]{1,128}$/.test(normalized) ? normalized : undefined;
}

function responseRequestId(error: unknown): string | undefined {
  if (!isAxiosError(error) || !error.response) return undefined;
  const body = error.response.data;
  const detail = body && typeof body === 'object'
    ? (body as { detail?: unknown }).detail
    : undefined;
  const bodyRequestId = detail && typeof detail === 'object'
    ? safeRequestId((detail as { request_id?: unknown }).request_id)
    : body && typeof body === 'object'
      ? safeRequestId((body as { request_id?: unknown }).request_id)
      : undefined;
  return bodyRequestId ?? safeRequestId(error.response.headers?.['x-request-id']);
}

function responseStatus(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const status = (data as { status?: unknown }).status;
  return typeof status === 'string' ? status.toLowerCase() : null;
}

export function accountDeletionResult(httpStatus: number, data: unknown): AccountDeletionResult {
  const status = responseStatus(data);
  if (status === 'deleted' || status === 'completed') {
    return { phase: 'completed' };
  }
  if (status === 'pending') return { phase: 'pending' };
  if (status === 'deleting') return { phase: 'deleting' };
  if (httpStatus === 202) return { phase: 'deleting' };
  return { phase: 'failed' };
}

export function accountDeletionResultFromError(error: unknown): AccountDeletionResult | null {
  return isAxiosError(error) && error.response?.status === 410
    ? { phase: 'deleting' }
    : null;
}

export function normalizeAccountDeletionError(
  error: unknown,
  actor: 'self' | 'admin',
): AccountDeletionFailure {
  const requestId = responseRequestId(error);
  if (!isAxiosError(error)) {
    return {
      code: 'incomplete',
      message: 'Не удалось подтвердить удаление. Данные не считаются удалёнными.',
      retryable: true,
      requestId,
    };
  }

  const httpStatus = error.response?.status;
  if (httpStatus === 410) {
    return {
      code: 'in_progress',
      message: 'Удаление уже выполняется. Проверь статус повторно.',
      retryable: true,
      httpStatus,
      requestId,
    };
  }
  if (httpStatus === 409) {
    return {
      code: 'protected',
      message: actor === 'self'
        ? 'Этот аккаунт защищён от самостоятельного удаления.'
        : 'Этого пользователя нельзя удалить из панели.',
      retryable: false,
      httpStatus,
      requestId,
    };
  }
  if ((actor === 'self' && (httpStatus === 400 || httpStatus === 403))
    || (actor === 'admin' && httpStatus === 422)) {
    return {
      code: 'invalid_confirmation',
      message: actor === 'self'
        ? 'Проверь подтверждение и текущий пароль.'
        : 'Проверь причину и точное совпадение логина.',
      retryable: false,
      httpStatus,
      requestId,
    };
  }
  if (httpStatus === 503 || httpStatus === undefined || httpStatus >= 500) {
    return {
      code: 'incomplete',
      message: 'Удаление завершено не во всех сервисах. Данные не считаются удалёнными.',
      retryable: true,
      httpStatus,
      requestId,
    };
  }
  return {
    code: 'rejected',
    message: 'Не удалось подтвердить удаление. Данные не считаются удалёнными.',
    retryable: false,
    httpStatus,
    requestId,
  };
}

export function accountDeletionErrorMessage(error: unknown, actor: 'self' | 'admin'): string {
  const failure = normalizeAccountDeletionError(error, actor);
  return failure.retryable ? `${failure.message} Повтор безопасен.` : failure.message;
}
